import { fetchAuthSession } from 'aws-amplify/auth';
import { type JwtPayload, jwtDecode } from 'jwt-decode';
import ErrorPage from 'pages/ErrorPage';
import { useEffect, useState } from 'react';

import { ROUTES, type Route } from './types';

type ProtectedRouteProps = {
    children: any;
    route: Route;
};

export const ROUTE_PERMISSIONS: Record<Route, string[]> = {
    [ROUTES.DEFAULT]: [
        'fleets:trigger-command',
        'fleets:create-mission',
        'fleets:cancel-mission',
        'fleets:archive-mission',
        'fleets:reset-mission',
        'fleets:read-mission',
        'fleets:start-stream-video',
        'fleets:report-video-stream-error',
        'fleets:continue-stream-video',
        'fleets:get-camera-image',
        'users:get-by-id',
        'teams:get-by-id',
        'fleets:get-by-id',
        'predefined-route:list',
        'hubs:list',
        'fleets:list-vehicles',
        'roles:list',
        'fleets:remove-vehicle-deviations',
    ],
    [ROUTES.VEHICLE]: [
        'fleets:trigger-command',
        'fleets:create-mission',
        'fleets:cancel-mission',
        'fleets:archive-mission',
        'fleets:reset-mission',
        'fleets:read-mission',
        'fleets:start-stream-video',
        'fleets:report-video-stream-error',
        'fleets:continue-stream-video',
        'fleets:get-camera-image',
        'users:get-by-id',
        'teams:get-by-id',
        'fleets:get-by-id',
        'predefined-route:list',
        'hubs:list',
        'fleets:list-vehicles',
        'roles:list',
        'fleets:remove-vehicle-deviations',
    ],
    [ROUTES.VEHICLE_BY_VIN]: [
        'fleets:trigger-command',
        'fleets:create-mission',
        'fleets:cancel-mission',
        'fleets:archive-mission',
        'fleets:reset-mission',
        'fleets:read-mission',
        'fleets:start-stream-video',
        'fleets:report-video-stream-error',
        'fleets:continue-stream-video',
        'fleets:get-camera-image',
        'users:get-by-id',
        'teams:get-by-id',
        'fleets:get-by-id',
        'predefined-route:list',
        'hubs:list',
        'fleets:list-vehicles',
        'roles:list',
        'fleets:remove-vehicle-deviations',
    ],
    [ROUTES.SIMULATIONS]: [
        'simulations:start-simulation',
        'simulations:stop-simulation',
        'simulations:upload-simulation',
        'simulations:initialize-simulation',
        'simulations:list-scenarios',
        'simulations:list-vehicles',
    ],
    [ROUTES.SIMULATIONS_V2]: [
        'simulations:start-simulation',
        'simulations:stop-simulation',
        'simulations:upload-simulation',
        'simulations:initialize-simulation',
        'simulations:list-scenarios',
        'simulations:list-vehicles',
    ],
    [ROUTES.SETTINGS]: [
        'users:create',
        'users:delete',
        'users:edit',
        'users:list',
        'teams:create',
        'teams:delete',
        'teams:edit',
        'teams:list',
        'fleets:create',
        'fleets:delete',
        'fleets:edit',
    ],
    [ROUTES.ERROR]: [],
};

type TokenPayload = JwtPayload & { claims: string };

const canAccessRoute = (claims: string, route: Route): boolean => {
    const allowedClaims = ROUTE_PERMISSIONS[route];

    if (!allowedClaims || allowedClaims.length === 0) {
        return false;
    }

    const claimsArray = claims.split(',').map(c => c.trim().split(':').slice(0, 2).join(':'));
    return claimsArray.some(claim => allowedClaims.includes(claim));
};

const checkPageAccess = async (route: Route) => {
    const accessToken = (await fetchAuthSession()).tokens?.accessToken;

    if (!accessToken) {
        return { isValid: false };
    }

    const decoded = jwtDecode<TokenPayload>(accessToken.toString());
    const decodedClaims = decoded.claims ? decoded.claims : '';

    const isValid = canAccessRoute(decodedClaims, route);

    return { isValid };
};

export const ProtectedRoute = ({ children, route }: ProtectedRouteProps) => {
    const [valid, setValid] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkPageAccess(route).then(({ isValid }) => {
            setValid(isValid);
            setLoading(false);
        });
    }, [route]);

    if (loading) {
        return null;
    }
    if (!valid) {
        return <ErrorPage />;
    }

    return children;
};
